import moment from 'moment';
import { useSessionStorage } from 'usehooks-ts';
import { DateFilters } from 'shared/components/table-filter-chips/models/date-filters';
import { FilterChipOption } from 'shared/components/table-filter-chips/multi-value-filter-chip/models/filter-chip-option';
import FilterSessionStorageKeys from 'shared/constants/filterSessionStorageKeys';
import SelectedOrganisationTreeNode from 'shared/features/organisation/models/selected-organisation-tree-node';
import KeyValuePair from 'shared/models/key-value-pair';

import useFaultsHistoryAvailableFiltersHook from 'features/faults-history/context/faults-history-available-filters-hook';
import { FaultCategory } from 'features/faults-history/domain/models/fault-category';
import FaultsHistoryFilter, { FaultsHistoryFilterValueType } from 'features/faults-history/domain/models/faults-history-filter';

export interface FaultsHistoryFilterState {
  activeFilters: FaultsHistoryFilter[];
  searchBarValue: string;
  dateFilters: DateFilters;
}

export interface FaultsHistoryFiltersHook {
  filters: FaultsHistoryFilterState;

  submitSearch: (query: string) => void;
  changeSelectedOrganisationUnits: (selectedOrganisationUnits: SelectedOrganisationTreeNode[]) => void;
  selectOption: (filterValueType: FaultsHistoryFilterValueType, keys: string[]) => void;
  setDateFilters: (newDateFilter: DateFilters) => void;
  clearFilters: () => void;

  categoryOptions: FilterChipOption[];
  faultTypeOptions: FilterChipOption[];
  typeOptions: FilterChipOption[];
  statusOptions: KeyValuePair[];

  selectedCategoryOptions: FilterChipOption[];
  selectedFaultTypeOptions: FilterChipOption[];
  selectedTypeOptions: FilterChipOption[];
  selectedOrganisationUnits: SelectedOrganisationTreeNode[];
  selectedStatusOption: KeyValuePair | undefined;
}

const useFaultsHistoryFilters = (): FaultsHistoryFiltersHook => {
  const submitSearch = (query: string) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      searchBarValue: query,
    }));
  };

  const splitCategoryCharacter = '-';
  const devicePrefix = 'device-';
  const systemPrefix = 'system-';
  const gatewayPrefix = 'gateway-';
  const connectorPrefix = 'connector-';

  const defaultFilters: FaultsHistoryFilterState = {
    activeFilters: [],
    searchBarValue: '',
    dateFilters: {
      dateFrom: moment().subtract(30, 'days').endOf('day'),
      dateTo: moment().endOf('day'),
    },
  };

  const [filters, setFilters] = useSessionStorage<FaultsHistoryFilterState>(
    FilterSessionStorageKeys.FAULTS_HISTORY_FILTERS,
    defaultFilters,
  );

  const {
    categoryOptions,
    deviceTypeOptions,
    systemTypeOptions,
    gatewayTypeOptions,
    connectorTypeOptions,
    deviceFaultTypeOptions,
    gatewayFaultTypeOptions,
    systemFaultTypeOptions,
    connectorFaultTypeOptions,
    statusOptions,
  } = useFaultsHistoryAvailableFiltersHook(filters.dateFilters.dateFrom, filters.dateFilters.dateTo);

  const [selectedOrganisationUnitsFromSessionStorage, setSelectedOrganisationUnitsFromSessionStorage] = useSessionStorage<
    SelectedOrganisationTreeNode[]
  >(FilterSessionStorageKeys.FAULTS_HISTORY_ORG_UNITS, []);

  const categoryFilter = filters.activeFilters.find((filter) => filter.filterValueType === FaultsHistoryFilterValueType.Category);

  const faultTypeOptions = (() => {
    const options: FilterChipOption[] = [];
    if (categoryFilter?.values.length) {
      if (categoryFilter.values.includes(FaultCategory.Device)) {
        options.push(...deviceFaultTypeOptions);
      }
      if (categoryFilter.values.includes(FaultCategory.Gateway)) {
        options.push(...gatewayFaultTypeOptions);
      }
      if (categoryFilter.values.includes(FaultCategory.System)) {
        options.push(...systemFaultTypeOptions);
      }
      if (categoryFilter.values.includes(FaultCategory.Connector)) {
        options.push(...connectorFaultTypeOptions);
      }
    } else {
      options.push(...deviceFaultTypeOptions, ...gatewayFaultTypeOptions, ...systemFaultTypeOptions, ...connectorFaultTypeOptions);
    }
    return options;
  })();

  const typeOptions = (() => {
    const options: FilterChipOption[] = [];
    if (categoryFilter?.values.length) {
      if (categoryFilter.values.includes(FaultCategory.Device)) {
        options.push(...deviceTypeOptions);
      }
      if (categoryFilter.values.includes(FaultCategory.Gateway)) {
        options.push(...gatewayTypeOptions);
      }
      if (categoryFilter.values.includes(FaultCategory.System)) {
        options.push(...systemTypeOptions);
      }
      if (categoryFilter.values.includes(FaultCategory.Connector)) {
        options.push(...connectorTypeOptions);
      }
    } else {
      options.push(...deviceTypeOptions, ...gatewayTypeOptions, ...systemTypeOptions, ...connectorTypeOptions);
    }
    return options;
  })();

  const handleTypeFilterSelection = (activeFilters: FaultsHistoryFilter[], keys: string[]) => {
    let updatedFilters = [...activeFilters];

    const groupedKeys = {
      device: keys.filter((key) => key.startsWith(devicePrefix)),
      gateway: keys.filter((key) => key.startsWith(gatewayPrefix)),
      system: keys.filter((key) => key.startsWith(systemPrefix)),
      connector: keys.filter((key) => key.startsWith(connectorPrefix)),
    };

    updatedFilters = updateFilter(
      updatedFilters,
      FaultsHistoryFilterValueType.DeviceType,
      groupedKeys.device.map((key) => key.split(splitCategoryCharacter)[1]),
    );

    updatedFilters = updateFilter(
      updatedFilters,
      FaultsHistoryFilterValueType.GatewayType,
      groupedKeys.gateway.map((key) => key.split(splitCategoryCharacter)[1]),
    );

    updatedFilters = updateFilter(
      updatedFilters,
      FaultsHistoryFilterValueType.SystemType,
      groupedKeys.system.map((key) => key.split(splitCategoryCharacter)[1]),
    );

    updatedFilters = updateFilter(
      updatedFilters,
      FaultsHistoryFilterValueType.ConnectorType,
      groupedKeys.connector.map((key) => key.split(splitCategoryCharacter)[1]),
    );

    const currentCategories = updatedFilters.find((f) => f.filterValueType === FaultsHistoryFilterValueType.Category)?.values || [];
    const categories = new Set<string>(currentCategories);

    if (groupedKeys.device.length > 0) categories.add(FaultCategory.Device);
    if (groupedKeys.gateway.length > 0) categories.add(FaultCategory.Gateway);
    if (groupedKeys.system.length > 0) categories.add(FaultCategory.System);
    if (groupedKeys.connector.length > 0) categories.add(FaultCategory.Connector);

    return updateFilter(updatedFilters, FaultsHistoryFilterValueType.Category, Array.from(categories));
  };

  const updateFilter = (filters: FaultsHistoryFilter[], type: FaultsHistoryFilterValueType, values: string[]): FaultsHistoryFilter[] => {
    const newFilters = filters.filter((f) => f.filterValueType !== type);
    if (values.length > 0) {
      newFilters.push({ filterValueType: type, values });
    }
    return newFilters;
  };

  const changeSelectedOrganisationUnits = (selectedOrganisationUnits: SelectedOrganisationTreeNode[]) => {
    setSelectedOrganisationUnitsFromSessionStorage(selectedOrganisationUnits);
    selectOption(
      FaultsHistoryFilterValueType.Location,
      selectedOrganisationUnits.map((x) => x.id),
    );
  };

  const clearFilters = () => {
    setFilters(defaultFilters);
    setSelectedOrganisationUnitsFromSessionStorage([]);
  };

  const setDateFilters = (newDateFilter: DateFilters) => {
    setFilters((prevState) => ({
      ...prevState,
      dateFilters: newDateFilter,
    }));
  };

  const selectOption = (filterValueType: FaultsHistoryFilterValueType, keys: string[]) => {
    setFilters((prevFilters) => {
      const isTypeFilter = [
        FaultsHistoryFilterValueType.DeviceType,
        FaultsHistoryFilterValueType.GatewayType,
        FaultsHistoryFilterValueType.SystemType,
        FaultsHistoryFilterValueType.ConnectorType,
      ].includes(filterValueType);

      const activeFilters = isTypeFilter
        ? handleTypeFilterSelection(prevFilters.activeFilters, keys)
        : updateFilter(prevFilters.activeFilters, filterValueType, keys);

      return {
        ...prevFilters,
        activeFilters,
      };
    });
  };

  const getSelectedOptions = (options: FilterChipOption[], filterType: FaultsHistoryFilterValueType): FilterChipOption[] => {
    const activeFilter = filters.activeFilters.find((f) => f.filterValueType === filterType);
    return activeFilter ? options.filter((x) => activeFilter.values.includes(x.key)) : [];
  };

  const selectedCategoryOptions = getSelectedOptions(categoryOptions, FaultsHistoryFilterValueType.Category);

  const selectedFaultTypeOptions = getSelectedOptions(faultTypeOptions, FaultsHistoryFilterValueType.FaultType);

  const findOptionsForFilter = (filterType: FaultsHistoryFilterValueType, prefix: string): FilterChipOption[] => {
    const activeFilter = filters.activeFilters.find((f) => f.filterValueType === filterType);

    if (!activeFilter) {
      return [];
    }

    const mappedOptions = activeFilter.values.map((value) => typeOptions.find((option) => option.key === `${prefix}${value}`));

    return mappedOptions.filter((option): option is FilterChipOption => option !== undefined);
  };

  const selectedTypeOptions = [
    ...findOptionsForFilter(FaultsHistoryFilterValueType.DeviceType, devicePrefix),
    ...findOptionsForFilter(FaultsHistoryFilterValueType.GatewayType, gatewayPrefix),
    ...findOptionsForFilter(FaultsHistoryFilterValueType.SystemType, systemPrefix),
    ...findOptionsForFilter(FaultsHistoryFilterValueType.ConnectorType, connectorPrefix),
  ];

  const activeStatusFilter = filters.activeFilters.find((f) => f.filterValueType === FaultsHistoryFilterValueType.Status);
  const selectedStatusOption = activeStatusFilter ? statusOptions.find((x) => activeStatusFilter.values.includes(x.key)) : undefined;

  return {
    filters,
    setDateFilters,
    selectOption,
    submitSearch,
    clearFilters,
    changeSelectedOrganisationUnits,
    selectedOrganisationUnits: selectedOrganisationUnitsFromSessionStorage,
    faultTypeOptions,
    typeOptions,
    statusOptions,
    selectedCategoryOptions,
    selectedFaultTypeOptions,
    selectedTypeOptions,
    selectedStatusOption,
    categoryOptions,
  };
};

export default useFaultsHistoryFilters;
